import { ExclamationCircleFilled } from "@ant-design/icons";
import {
  FeatureFlag,
  SubscriptionScheduledChangeType,
  SubscriptionUpdateType,
  SubscriptionUser,
  WebDashboardEligibility,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { Dropdown, Menu, message } from "antd";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import styled from "styled-components";

import {
  Alert,
  ConfirmBox,
  FlexColumn,
  Typography,
  Button,
  FlexRow,
  CardIcon,
  BillingUpgradeToProIcon,
  BillingManageLicensesUserIcon,
  EllipsisMenu,
  UserList,
  Breadcrumb,
  BreadcrumbItem,
} from "../../../components";
import { AVATAR_COLORS as avatarColors, COLORS } from "../../../constants";
import { BContext } from "../../../types";
import {
  planNameFromType,
  useTrack,
  numberWithCommas,
  pluralize,
  capitalize,
} from "../../../utils";
import { BillingContext } from "../Billing";
import { AddSeatsModal } from "../components/AddSeatsModal";
import { AddWebDashboardModal } from "../components/AddWebDashboardModal";
import { CancelWebDashboardModal } from "../components/CancelWebDashboardModal";
import { ContactSalesModal } from "../components/ContactSalesModal";
import { HelpContent } from "../components/HelpContent";
import {
  ADD_AI_DASHBOARDS_PARAM,
  AI_DASHBOARDS_BILLING_PAGE_GATE_PROPS,
  getSubscriptionPageAiDashboardsPrompt,
} from "../planChooserSource.utils";
import {
  getTooManyDashboardsMessage,
  SELF_SERVE_WEB_DASHBOARD_LIMIT,
} from "../webDashboardEligibility.utils";

import { getPendingLicenseDrop } from "./members.utils";

const Container = styled.div`
  color: ${COLORS.black85};
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
`;

const SubscriptionInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 60%;
`;

const HelpContainer = styled.div`
  width: 35%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AlertMsg = styled(Alert)`
  margin-bottom: 8px;
  border: none;

  &.ant-alert-error {
    .updatePaymentLink {
      font-weight: bold;
    }
  }

  &.ant-alert-success {
    font-weight: 600;
    background-color: ${COLORS.seafoamBg};

    .ant-alert-message {
      color: ${COLORS.seafoam};
    }
  }
`;

const Section = styled.div`
  display: flex;
  width: 100%;

  flex-direction: column;
  margin-bottom: 20px;

  .sectionTitle {
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    margin-bottom: 4px;
  }

  .content {
    color: ${COLORS.black65};
    flex: 1;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background: ${COLORS.black2};

    padding: 16px;
    border-radius: 5px;

    &.noPadding {
      padding: 0px;
    }

    .ant-progress-inner {
      background: ${COLORS.black15};
    }
  }
`;

/* Attached primary button + ⋮ segment, same pattern as the old Manage Licenses header. */
const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1px;
  margin-right: 16px;

  .ant-btn {
    border-radius: 0px !important;
    padding-left: 12px;
    padding-right: 12px;

    &:first-child {
      border-top-left-radius: 5px !important;
      border-bottom-left-radius: 5px !important;
    }

    &:last-child {
      border-top-right-radius: 5px !important;
      border-bottom-right-radius: 5px !important;
    }
  }
`;

const ManageUserContainer = styled(FlexRow)`
  justify-content: space-between;
  flex: 1;
  background: white;
  border-radius: 5px;
`;

const LinkTypograhy = styled(Typography)`
  &:hover {
    color: ${COLORS.coeblue4};
  }
`;

const StackedAvatars = styled.div`
  display: flex;

  list-style-type: none;
  margin: auto;
  padding: 0px;
  flex-direction: row;
  margin-left: 16px;
`;

export const SubscriptionInfoV2 = () => {
  const track = useTrack();

  const { value: enableBillingMembersRedesign } = useFeatureGate(
    FeatureFlag.EnableBillingMembersRedesign
  );

  const {
    basePath,
    currentUserEmail,
    userSubscription,
    plan_type,
    enableSubscriptionUpdate,
    due_invoices_count,
    due_invoice_error_type,
    usersInCurrentPlan,
    currentUserPlanManager,
    setContactSupportInfoBox,
    updateSubscription,
    syncBilling,

    isManager,
    subscriptions,
    isFreeUser,
    eventFrom,
    billingMetadata,
  } = useContext(BillingContext) as BContext;

  const history = useHistory();

  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  const subscriptionDetails = userSubscription?.subscription_details || null;

  const planType = subscriptionDetails?.plan_type || "";

  const [showManagerPrompt, setShowManagerPrompt] = useState<boolean>(false);

  // Add/Remove seats dialog (redesign): "Add seats" link + ⋮ dropdown with "Remove seats".
  const [seatsModal, setSeatsModal] = useState<null | "add" | "remove">(null);

  const [showRemoveWebDashboardModal, setShowRemoveWebDashboardModal] = useState<boolean>(false);
  const [showAddWebDashboardModal, setShowAddWebDashboardModal] = useState<boolean>(false);
  // AI Dashboards upgrade CTA followed by someone who can't buy the add-on here themselves.
  const [showAiDashboardsAskManager, setShowAiDashboardsAskManager] = useState<boolean>(false);
  const [showAiDashboardsContactSales, setShowAiDashboardsContactSales] = useState<boolean>(false);
  const aiDashboardsPromptHandled = useRef<boolean>(false);

  useEffect(() => {
    // The ?from= Billing was opened with: a seatless admin following Email 1's Manage Licenses link
    // comes through here (Subscriptions -> Manage) on the way to Manage Licenses.
    track("billing_subscription_viewed", { event_from: eventFrom });
    document.title = "Plans & Billing - Coefficient Workspace";
  }, []);

  const planName = planNameFromType(planType || "");

  const nextBillingCycleDateStr = subscriptionDetails
    ? moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")
    : undefined;

  const nextBillingCycleInterval =
    subscriptionDetails?.has_scheduled_changes &&
    subscriptionDetails.scheduled_change_type ===
      SubscriptionScheduledChangeType.DowngradeToMonthlyBilling
      ? "monthly"
      : subscriptionDetails?.billing_interval;

  const scheduledCancelRenewalOnDate = subscriptionDetails?.next_billing_cycle_dt ? (
    <>
      {" "}
      on{" "}
      <strong style={{ color: COLORS.black85 }}>
        {moment(subscriptionDetails.next_billing_cycle_dt).format("MMM DD, YYYY")}
      </strong>
    </>
  ) : null;

  const showScheduledCancelBanner =
    Boolean(subscriptionDetails?.has_scheduled_changes) &&
    subscriptionDetails?.scheduled_change_type ===
      SubscriptionScheduledChangeType.CancelSubscription;

  const firstInvoiceDateStr = subscriptionDetails?.subscription_active_since_dt
    ? moment(subscriptionDetails?.subscription_active_since_dt).format("MMM DD, YYYY")
    : undefined;

  const handleChangePlan = () => {
    if (!enableSubscriptionUpdate) {
      setContactSupportInfoBox({ title: "Change Plan" });
      return;
    }
    if (!isManager) {
      setShowManagerPrompt(true);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    searchParams.set("from", "billing");

    history.push({
      pathname: `${basePath}/plans`,
      search: searchParams.toString(),
    });
  };

  const handlePaymentAndBilling = (e: React.MouseEvent) => {
    track("billing_settings_clicked");
    const searchParams = new URLSearchParams(window.location.search);
    if (!isManager) {
      e.preventDefault();
      setShowManagerPrompt(true);
      return;
    }

    history.push({
      pathname: `${basePath}/billing`,
      search: searchParams.toString(),
    });
  };

  // Also invoked from the seats dropdown, whose antd Menu passes a MenuInfo (no preventDefault).
  const handleManageTeam = (e?: { preventDefault?: () => void }) => {
    const searchParams = new URLSearchParams(window.location.search);

    track("billing_manage_team_clicked");
    if (!isManager) {
      e?.preventDefault?.();
      setShowManagerPrompt(true);
      return;
    }

    if (enableBillingMembersRedesign) {
      // Redesign: open the Add seats dialog right here instead of redirecting.
      // Same self-serve gate as the old Manage Licenses menu: sales-managed plans
      // (checkout_mode contact_us) get the contact-support box instead.
      if (!enableSubscriptionUpdate) {
        setContactSupportInfoBox({ title: "Manage Licenses" });
        return;
      }
      setSeatsModal("add");
      return;
    }

    history.push({
      pathname: `${basePath}/team/manage`,
      search: searchParams.toString(),
    });
  };

  const handleRemoveSeats = () => {
    if (!isManager) {
      setShowManagerPrompt(true);
      return;
    }
    if (!enableSubscriptionUpdate) {
      setContactSupportInfoBox({ title: "Manage Licenses" });
      return;
    }
    setSeatsModal("remove");
  };

  const [undoingCancel, setUndoingCancel] = useState(false);

  const handleUndoCancellation = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id || !enableSubscriptionUpdate) {
      return;
    }
    setUndoingCancel(true);
    try {
      const res = await updateSubscription({
        update_type: SubscriptionUpdateType.UndoCancellation,
        subscription_id: subscriptionDetails.subscription_id,
      });
      if (res?.is_success) {
        track("billing_cancel_undo_completed", {
          current_plan_type: subscriptionDetails?.plan_type,
          event_from: "subscription_page",
        });
        await syncBilling();
        message.success("Cancellation has been undone.");
      } else {
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setUndoingCancel(false);
    }
  }, [subscriptionDetails, enableSubscriptionUpdate, updateSubscription, syncBilling, track]);

  const [cancelingLicenseReduction, setCancelingLicenseReduction] = useState(false);

  const handleCancelLicenseReduction = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id || !enableSubscriptionUpdate) {
      return;
    }
    setCancelingLicenseReduction(true);
    // Read before syncBilling refreshes it.
    const { effectiveLicenses } = getPendingLicenseDrop(subscriptionDetails);
    const restored = (subscriptionDetails.max_users ?? 0) - effectiveLicenses;
    try {
      const res = await updateSubscription({
        update_type: SubscriptionUpdateType.RemoveScheduledChanges,
        subscription_id: subscriptionDetails.subscription_id,
      });
      if (res?.is_success) {
        track("billing_manage_team_scheduled_downgrade_cancelled", {
          current_plan_type: subscriptionDetails?.plan_type,
          event_from: "subscription_page",
        });
        await syncBilling();
        message.success(`${restored} license${pluralize(restored)} restored`);
      } else {
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setCancelingLicenseReduction(false);
    }
  }, [subscriptionDetails, enableSubscriptionUpdate, updateSubscription, syncBilling, track]);

  const maxUsersCount = subscriptionDetails?.max_users ?? 0;

  const isPlanOwner = isManager; // currentUserPlanManager?.email === currentUserEmail;

  const hasWebDashboardAddon = Boolean(subscriptionDetails?.has_web_dashboard_addon);

  const webDashboardScheduledRemoval = Boolean(
    subscriptionDetails?.web_dashboard_scheduled_removal
  );

  // The Remove affordance is only for self-serve purchases (sales-sold addons are managed by
  // sales), and follows the same owner-only gating as the other plan management actions.
  const canRemoveWebDashboards =
    enableSelfServeAiDashboards &&
    Boolean(subscriptionDetails?.web_dashboard_self_serve) &&
    isPlanOwner &&
    enableSubscriptionUpdate &&
    !webDashboardScheduledRemoval;

  const ownerHasSeatOnPlan = Boolean(
    usersInCurrentPlan.find(user => user.user.email === currentUserPlanManager?.email)
  );

  const isPlanOwnerWithoutLicenseOnPlan = isPlanOwner && !ownerHasSeatOnPlan;

  let sortedUsersInPlan = [...usersInCurrentPlan, ...(userSubscription?.invite_users ?? [])].sort(
    (a, b) => {
      const bName = `${b.user.first_name} ${b.user.last_name}`;
      const aName = `${a.user.first_name} ${a.user.last_name}`;

      if (aName < bName) {
        return -1;
      }
      if (aName > bName) {
        return 1;
      }
      return 0;
    }
  );

  const currentPlanManager = sortedUsersInPlan.find(
    u => u.user.user_id === currentUserPlanManager?.user_id
  );

  if (currentPlanManager) {
    sortedUsersInPlan = sortedUsersInPlan.filter(
      u => u.user.user_id !== currentPlanManager?.user.user_id
    );
    sortedUsersInPlan.unshift(currentPlanManager);
  }

  const renderUserRole = (user: SubscriptionUser) => {
    let role = "";

    if (user.user_id === currentPlanManager?.user.user_id) {
      role = "Plan Owner";
    }

    return (
      <Typography color="textSecondary" fontWeight={500}>
        {role}
      </Typography>
    );
  };

  const addOnSuffix =
    enableSelfServeAiDashboards && hasWebDashboardAddon ? " (+ AI Dashboards Add-on)" : "";
  const PlanOwerSubsciptionPlanText = isPlanOwnerWithoutLicenseOnPlan
    ? `You're the owner of this ${planName} plan${addOnSuffix}`
    : `You're on the ${planName} plan${addOnSuffix}`;

  // AI Dashboards upgrade CTAs land here: eligible Pro subscribers via ?add_ai_dashboards=1 (the
  // plan chooser's toggle is easy to miss), and seat holders who aren't plan managers redirected
  // off the plan chooser. The plan manager of an eligible plan gets the confirm modal; a seat
  // holder is asked to go to their plan manager; a plan that can't self-serve the add-on gets
  // contact sales. Handled once per visit; the add param is then dropped so a reload or Back
  // doesn't re-open it.
  useEffect(() => {
    if (aiDashboardsPromptHandled.current || !billingMetadata || !subscriptionDetails) {
      return;
    }
    aiDashboardsPromptHandled.current = true;
    const searchParams = new URLSearchParams(window.location.search);
    const hasAddParam = searchParams.has(ADD_AI_DASHBOARDS_PARAM);
    const prompt = enableSelfServeAiDashboards
      ? getSubscriptionPageAiDashboardsPrompt({
          from: eventFrom,
          hasAddParam,
          eligibility: billingMetadata.web_dashboard_eligibility,
          isManager: Boolean(isPlanOwner),
        })
      : null;
    if (prompt === "add_on_modal") {
      setShowAddWebDashboardModal(true);
    } else if (prompt === "ask_manager") {
      setShowAiDashboardsAskManager(true);
    } else if (prompt === "contact_sales") {
      setShowAiDashboardsContactSales(true);
    }
    if (hasAddParam) {
      searchParams.delete(ADD_AI_DASHBOARDS_PARAM);
      history.replace({ search: searchParams.toString() });
    }
  }, [billingMetadata, subscriptionDetails]);

  const renderPendingInvoiceCard = () => {
    if (!due_invoices_count || due_invoices_count <= 0) {
      return null;
    }

    const formatDollarAmount = (amount: number | null | undefined) => {
      if (amount === null || amount === undefined) {
        return "";
      }
      return `$${numberWithCommas(amount)}`;
    };

    const getCardConfig = () => {
      switch (due_invoice_error_type) {
        case "payment_failed":
          return {
            type: "error" as const,
            message: "Payment failed",
            description: (
              <>
                Your last payment of {formatDollarAmount(subscriptionDetails?.recurring_amount_usd)}{" "}
                couldn't be processed to the card ending with{" "}
                {subscriptionDetails?.cc_last_4_digits} &nbsp;
              </>
            ),
            action: (
              <Link
                className="updatePaymentLink"
                to={{ pathname: `${basePath}/billing`, search: window.location.search }}
                onClick={e => {
                  if (!isManager) {
                    e.preventDefault();
                    setShowManagerPrompt(true);
                  }
                }}
              >
                Update Payment Method
              </Link>
            ),
          };

        case "pending_offline_payment":
          return {
            type: "warning" as const,
            message: "Unpaid invoice",
            description: (
              <>
                Your last invoice has not been marked as paid yet. Please contact support to resolve
                this. &nbsp;
                <a
                  href="mailto:support@coefficient.io"
                  className="updatePaymentLink"
                  target="_blank"
                >
                  Contact Support
                </a>
              </>
            ),
          };

        case "no_payment_method":
          return {
            type: "error" as const,
            message: "Please add a payment method",
            description: <>Your subscription is not active until you add a payment method.</>,
            action: (
              <Link
                className="updatePaymentLink"
                to={{ pathname: `${basePath}/billing`, search: window.location.search }}
                onClick={e => {
                  if (!isManager) {
                    e.preventDefault();
                    setShowManagerPrompt(true);
                  }
                }}
              >
                Add Payment Method
              </Link>
            ),
          };

        case "other":
        default:
          return {
            type: "warning" as const,
            message: "Payment not received",
            description: (
              <>
                We didn't receive your payment, please{" "}
                <a
                  href="mailto:support@coefficient.io"
                  className="updatePaymentLink"
                  target="_blank"
                >
                  Contact Support
                </a>
              </>
            ),
          };
      }
    };

    const config = getCardConfig();

    return (
      <AlertMsg
        message={config.message}
        description={config.description}
        type={config.type}
        showIcon
        icon={<ExclamationCircleFilled />}
        action={config.action}
      />
    );
  };
  return (
    <Container>
      <Breadcrumb>
        {!!subscriptions?.other_subscriptions?.length && (
          <BreadcrumbItem>
            <Link to={{ pathname: `${basePath}/subscriptions`, search: window.location.search }}>
              Subscriptions
            </Link>
          </BreadcrumbItem>
        )}
        <BreadcrumbItem>Plans & Billing</BreadcrumbItem>
      </Breadcrumb>

      <Typography fontSize="26px" fontWeight={700} style={{ marginBottom: "52px" }}>
        Plans & Billing
      </Typography>

      <Content>
        <SubscriptionInfoContainer>
          {showScheduledCancelBanner && (
            <AlertMsg
              type="warning"
              message={<>Your {planName} plan has been canceled</>}
              description={
                <>
                  Your {planName} plan will be canceled at the end of your current billing cycle
                  {scheduledCancelRenewalOnDate} and you'll be downgraded to the free plan
                  <br />
                  {isPlanOwner && enableSubscriptionUpdate && (
                    <>
                      {" "}
                      <Button
                        type="link"
                        noPadding
                        loading={undoingCancel}
                        onClick={handleUndoCancellation}
                        style={{ fontWeight: 600, padding: 0, height: "auto" }}
                      >
                        Undo cancellation
                      </Button>
                    </>
                  )}
                </>
              }
              showIcon
              icon={<ExclamationCircleFilled />}
              style={{ marginBottom: 16 }}
            />
          )}
          {isPlanOwner ? (
            <>
              <Section>
                {isFreeUser && (
                  <AlertMsg
                    type="info"
                    style={{ padding: "12px 16px", marginBottom: "16px" }}
                    description={
                      <FlexColumn gap={0}>
                        <Typography fontWeight={600}>
                          {`You're on the ${planNameFromType(plan_type)} plan`}
                        </Typography>
                        <Typography color="textSecondary">
                          {`Add yourself to your ${planName} plan to get the most out of Coefficient`}
                        </Typography>
                      </FlexColumn>
                    }
                  />
                )}

                <div className="sectionTitle">Subscription Plan</div>

                {subscriptionDetails?.has_scheduled_changes &&
                  subscriptionDetails?.scheduled_change_type === "downgrade_to_starter" && (
                    <AlertMsg
                      type="warning"
                      description={
                        <Typography fontWeight={600}>
                          Your plan will be downgraded to Starter on{" "}
                          {moment(subscriptionDetails?.next_billing_cycle_dt).format(
                            "MMM DD, YYYY"
                          )}
                        </Typography>
                      }
                    />
                  )}

                {subscriptionDetails?.has_scheduled_changes &&
                  subscriptionDetails?.scheduled_change_type === "downgrade_num_licenses" && (
                    <AlertMsg
                      type="warning"
                      description={
                        <FlexRow justifyContent="space-between" gap={2}>
                          <Typography fontWeight={600}>
                            You have {subscriptionDetails?.max_users} license
                            {pluralize(subscriptionDetails?.max_users)} now, reducing to{" "}
                            {subscriptionDetails?.scheduled_changes_info?.num_licenses} on{" "}
                            {moment(subscriptionDetails?.next_billing_cycle_dt).format(
                              "MMM D, YYYY"
                            )}
                          </Typography>
                          {isPlanOwner && enableSubscriptionUpdate && (
                            <Button
                              type="link"
                              noPadding
                              loading={cancelingLicenseReduction}
                              onClick={handleCancelLicenseReduction}
                              style={{
                                fontWeight: 600,
                                padding: 0,
                                height: "auto",
                                flexShrink: 0,
                              }}
                            >
                              Undo
                            </Button>
                          )}
                        </FlexRow>
                      }
                    />
                  )}

                {enableSelfServeAiDashboards && webDashboardScheduledRemoval && (
                  <AlertMsg
                    type="warning"
                    description={
                      <Typography fontWeight={600}>
                        AI Dashboards will be removed on{" "}
                        {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")}.
                        Your dashboards will be archived — nothing is deleted.
                      </Typography>
                    }
                  />
                )}
                <div className="content">
                  <FlexColumn gap={2} style={{ width: "100%" }}>
                    <FlexRow style={{ justifyContent: "space-between", flex: 1 }}>
                      <FlexColumn gap={0}>
                        <Typography color="textPrimary" variant="body2" fontWeight={600}>
                          {PlanOwerSubsciptionPlanText}
                        </Typography>
                        {firstInvoiceDateStr && (
                          <Typography
                            color="inherit"
                            variant="body2"
                          >{`Since ${firstInvoiceDateStr}`}</Typography>
                        )}
                      </FlexColumn>

                      <Button type="link" className="light" onClick={handleChangePlan}>
                        Change plan
                      </Button>
                    </FlexRow>
                    <ManageUserContainer>
                      <FlexRow gap={2} style={{ padding: "16px" }}>
                        <BillingManageLicensesUserIcon />
                        <FlexColumn style={{ width: 200 }} gap={0}>
                          <Typography fontWeight={700} color="inherit">
                            {subscriptionDetails?.plan_type === "starter"
                              ? `Add more users to your plan`
                              : `${maxUsersCount} user license${pluralize(
                                  maxUsersCount
                                )} on your plan`}
                          </Typography>
                        </FlexColumn>
                      </FlexRow>
                      {subscriptionDetails?.plan_type === "starter" ? (
                        <Button
                          type="primary"
                          onClick={handleChangePlan}
                          style={{ marginRight: "16px" }}
                        >
                          <BillingUpgradeToProIcon />
                          Upgrade to Pro
                        </Button>
                      ) : enableBillingMembersRedesign ? (
                        <ButtonGroup>
                          <Button type="primary" onClick={handleManageTeam}>
                            Add seats
                          </Button>
                          <Dropdown
                            trigger={["click"]}
                            placement="bottomRight"
                            overlay={
                              <Menu>
                                <Menu.Item key="remove-seats" onClick={handleRemoveSeats}>
                                  Remove seats
                                </Menu.Item>
                              </Menu>
                            }
                          >
                            <Button type="primary">
                              <EllipsisMenu height={"12px"} fill="currentColor" />
                            </Button>
                          </Dropdown>
                        </ButtonGroup>
                      ) : (
                        <Button type="link" onClick={handleManageTeam}>
                          Manage licenses
                        </Button>
                      )}
                    </ManageUserContainer>
                    {enableSelfServeAiDashboards && hasWebDashboardAddon && (
                      <ManageUserContainer>
                        <FlexRow gap={2} style={{ padding: "16px" }}>
                          <FlexColumn style={{ width: 200 }} gap={0}>
                            <Typography fontWeight={700} color="inherit">
                              AI Dashboards
                            </Typography>
                            {subscriptionDetails?.web_dashboard_monthly_price_usd != null && (
                              <Typography color="textSecondary">
                                {`$${numberWithCommas(
                                  subscriptionDetails.web_dashboard_monthly_price_usd
                                )}/mo`}
                              </Typography>
                            )}
                          </FlexColumn>
                        </FlexRow>
                        {canRemoveWebDashboards && (
                          <Button type="link" onClick={() => setShowRemoveWebDashboardModal(true)}>
                            Remove add-on
                          </Button>
                        )}
                      </ManageUserContainer>
                    )}
                  </FlexColumn>
                </div>
              </Section>
              <Section>
                <div className="sectionTitle">Payment & Billing</div>
                {renderPendingInvoiceCard()}
                {subscriptionDetails?.has_scheduled_changes &&
                subscriptionDetails.scheduled_change_type ===
                  SubscriptionScheduledChangeType.DowngradeToMonthlyBilling ? (
                  <AlertMsg
                    type="warning"
                    message={
                      <Typography fontWeight={600}>
                        Your billing frequency will be changed to monthly starting on{" "}
                        {moment(subscriptionDetails?.next_billing_cycle_dt).format("MMM DD, YYYY")}
                      </Typography>
                    }
                  />
                ) : null}
                <div className="content">
                  <FlexColumn gap={0}>
                    {subscriptionDetails?.current_paying_amount_usd ? (
                      <Typography>{`You are paying $${numberWithCommas(
                        subscriptionDetails?.current_paying_amount_usd ?? 0
                      )} per ${
                        subscriptionDetails?.billing_interval === "monthly" ? "month" : "year"
                      }`}</Typography>
                    ) : (
                      <noscript />
                    )}
                    {nextBillingCycleDateStr && (
                      <Typography color="textSecondary">{`Next payment ${nextBillingCycleDateStr} (${nextBillingCycleInterval})`}</Typography>
                    )}

                    {subscriptionDetails?.cc_type && (
                      <FlexRow gap={1} style={{ marginTop: "24px" }}>
                        <CardIcon type={subscriptionDetails?.cc_type || ""} />{" "}
                        <Typography style={{ wordBreak: "break-all" }} lineClamp={1}>
                          {capitalize(subscriptionDetails?.cc_type.replace("_", " "))}
                        </Typography>
                        <Typography style={{ marginRight: 8 }}>
                          (...{subscriptionDetails?.cc_last_4_digits})
                        </Typography>
                      </FlexRow>
                    )}
                  </FlexColumn>
                  <Button type="link" onClick={handlePaymentAndBilling}>
                    Manage billing
                  </Button>
                </div>
              </Section>
            </>
          ) : (
            <>
              <Section>
                <div className="sectionTitle">Subscription Plan</div>

                <div className="content">
                  <FlexColumn gap={2} style={{ width: "100%" }}>
                    <FlexRow style={{ justifyContent: "space-between", flex: 1 }}>
                      <FlexColumn gap={0}>
                        <Typography
                          color="textPrimary"
                          fontWeight={600}
                          variant="body2"
                        >{`You're on the ${planName} plan`}</Typography>
                        {firstInvoiceDateStr && (
                          <Typography
                            color="inherit"
                            variant="body2"
                          >{`Since ${firstInvoiceDateStr}`}</Typography>
                        )}
                        <Typography
                          color="inherit"
                          variant="body2"
                        >{`Plan managed by ${currentUserPlanManager?.first_name} ${currentUserPlanManager?.last_name}`}</Typography>
                      </FlexColumn>
                    </FlexRow>
                  </FlexColumn>
                </div>
              </Section>
              <Section>
                <div className="sectionTitle">{`${sortedUsersInPlan.length} user${pluralize(
                  sortedUsersInPlan.length
                )} on your ${planName} plan`}</div>

                <FlexColumn gap={0}>
                  {sortedUsersInPlan.map((user, i) => (
                    <UserList
                      key={i}
                      avatarColor={avatarColors[i % avatarColors.length]}
                      name={`${user.user?.first_name || user.user.email} ${
                        user.user?.last_name || ""
                      }`}
                      email={user.user?.email || ""}
                      userId={user.user?.user_id || ""}
                      plan={user.plan_type}
                      footer={
                        <FlexRow gap={2} style={{ justifyContent: "flex-end" }}>
                          <FlexRow gap={2} style={{ justifyContent: "space-between" }}>
                            {renderUserRole(user.user)}
                          </FlexRow>
                        </FlexRow>
                      }
                    />
                  ))}
                </FlexColumn>
              </Section>
            </>
          )}
        </SubscriptionInfoContainer>

        <HelpContent />
      </Content>

      <ConfirmBox
        title={
          currentUserPlanManager
            ? `${planNameFromType(subscriptionDetails?.plan_type ?? "")} plan managed by ${
                currentUserPlanManager?.first_name
              } ${currentUserPlanManager?.last_name}`
            : "Please assign at least one user"
        }
        visible={showManagerPrompt}
        onOk={() => setShowManagerPrompt(false)}
        onCancel={() => setShowManagerPrompt(false)}
        okText="Got it"
        width={340}
      >
        {currentUserPlanManager
          ? `Please reach out to ${
              currentUserPlanManager?.first_name ?? ""
            } to make any changes to the
        plan or to add/remove user licenses.`
          : "Please assign at least one user before modifying the plan and billing settings"}
      </ConfirmBox>

      <AddSeatsModal
        visible={seatsModal !== null}
        mode={seatsModal ?? "add"}
        onClose={() => setSeatsModal(null)}
      />

      <CancelWebDashboardModal
        visible={showRemoveWebDashboardModal}
        onClose={() => setShowRemoveWebDashboardModal(false)}
      />
      <ConfirmBox
        title={`Add AI Dashboards to your ${planName} plan`}
        visible={showAiDashboardsAskManager}
        onOk={() => setShowAiDashboardsAskManager(false)}
        onCancel={() => setShowAiDashboardsAskManager(false)}
        okText="Got it"
        width={380}
      >
        {`${
          currentUserPlanManager
            ? `Ask ${currentUserPlanManager.first_name ?? "your plan manager"}`
            : "Ask your plan manager"
        } to add the AI Dashboards add-on from Plans & Billing. It's billed per builder (max ${SELF_SERVE_WEB_DASHBOARD_LIMIT} active dashboards per builder).`}
      </ConfirmBox>
      <ContactSalesModal
        showModal={showAiDashboardsContactSales}
        description={
          billingMetadata?.web_dashboard_eligibility === WebDashboardEligibility.TooManyDashboards
            ? getTooManyDashboardsMessage(billingMetadata?.web_dashboard_active_count)
            : `To add AI Dashboards to your ${planName} plan, contact sales.`
        }
        hideTextarea
        gateProps={AI_DASHBOARDS_BILLING_PAGE_GATE_PROPS}
        onCancel={() => setShowAiDashboardsContactSales(false)}
      />
      {showAddWebDashboardModal && (
        <AddWebDashboardModal
          visible={showAddWebDashboardModal}
          onClose={() => setShowAddWebDashboardModal(false)}
        />
      )}
    </Container>
  );
};
